import { useEffect, useRef, useState } from 'react';

/**
 * Hook personalizado para detectar inactividad del usuario
 * @param {number} timeout - Tiempo de inactividad en milisegundos (por defecto 3 minutos)
 * @param {boolean} enabled - Si el detector está habilitado
 * @returns {boolean} - true si hay inactividad, false si hay actividad
 */
export const useInactivityDetector = (timeout = 3 * 60 * 1000, enabled = true) => {
  const [isInactive, setIsInactive] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      setIsInactive(false);
      return;
    }

    const resetTimer = () => {
      // Si estaba inactivo, lo marcamos como activo
      if (isInactive) {
        setIsInactive(false);
      }

      // Limpiamos el timeout anterior
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Iniciamos un nuevo timeout
      timeoutRef.current = setTimeout(() => {
        setIsInactive(true);
      }, timeout);
    };

    // Eventos a escuchar para detectar actividad
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Añadir listeners a todos los eventos
    events.forEach((event) => {
      document.addEventListener(event, resetTimer, true);
    });

    // Iniciar el timer al montar
    resetTimer();

    // Cleanup: remover listeners y limpiar timeout
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, resetTimer, true);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [timeout, enabled, isInactive]);

  return isInactive;
};
